import { Edge, FamilyTreeNode } from '@/components/diagrams/FlowChart';

import { MarriageGroupData } from '../components/MarriageGroupNode';
import { FamilyTreeNodeConfig } from '../types';
import {
  CHILDREN_COUPLE_HANDLE_ID,
  CHILDREN_SPOUSE_1_EX_HANDLE_ID,
  CHILDREN_SPOUSE_2_EX_HANDLE_ID,
  PARENTS_SPOUSE_1_EX_HANDLE_ID,
  PARENTS_SPOUSE_1_HANDLE_ID,
  PARENTS_SPOUSE_2_EX_HANDLE_ID,
  PARENTS_SPOUSE_2_HANDLE_ID,
} from './elkLayout.constants';

/**
 * Gets the specific people connected by a handle in a marriage group node
 */
function getPeopleForHandle(
  marriageGroupData: MarriageGroupData,
  handleId: string | undefined,
  explicitlyHiddenPersonIds: Set<string>
): string[] {
  if (!handleId) {
    // No handle specified - connect to all non-hidden people in the group
    const people: string[] = [];
    if (!explicitlyHiddenPersonIds.has(marriageGroupData.spouse1.id)) {
      people.push(marriageGroupData.spouse1.id);
    }
    if (
      marriageGroupData.spouse2 &&
      !explicitlyHiddenPersonIds.has(marriageGroupData.spouse2.id)
    ) {
      people.push(marriageGroupData.spouse2.id);
    }
    return people;
  }

  // Handle specifies which person(s) the edge connects to
  if (
    handleId === PARENTS_SPOUSE_1_HANDLE_ID ||
    handleId === CHILDREN_SPOUSE_1_EX_HANDLE_ID
  ) {
    // Connects to spouse1 only
    return !explicitlyHiddenPersonIds.has(marriageGroupData.spouse1.id)
      ? [marriageGroupData.spouse1.id]
      : [];
  }

  if (
    handleId === PARENTS_SPOUSE_2_HANDLE_ID ||
    handleId === CHILDREN_SPOUSE_2_EX_HANDLE_ID
  ) {
    // Connects to spouse2 only
    return marriageGroupData.spouse2 &&
      !explicitlyHiddenPersonIds.has(marriageGroupData.spouse2.id)
      ? [marriageGroupData.spouse2.id]
      : [];
  }

  if (handleId === PARENTS_SPOUSE_1_EX_HANDLE_ID) {
    // Connects to spouse1's ex-spouses
    return marriageGroupData.spouse1ExSpouses
      .filter((ex) => !explicitlyHiddenPersonIds.has(ex.id))
      .map((ex) => ex.id);
  }

  if (handleId === PARENTS_SPOUSE_2_EX_HANDLE_ID) {
    // Connects to spouse2's ex-spouses
    return marriageGroupData.spouse2ExSpouses
      .filter((ex) => !explicitlyHiddenPersonIds.has(ex.id))
      .map((ex) => ex.id);
  }

  if (handleId === CHILDREN_COUPLE_HANDLE_ID) {
    // Connects to the couple (both spouse1 and spouse2 if present)
    const people: string[] = [];
    if (!explicitlyHiddenPersonIds.has(marriageGroupData.spouse1.id)) {
      people.push(marriageGroupData.spouse1.id);
    }
    if (
      marriageGroupData.spouse2 &&
      !explicitlyHiddenPersonIds.has(marriageGroupData.spouse2.id)
    ) {
      people.push(marriageGroupData.spouse2.id);
    }
    return people;
  }

  // Unknown handle - connect to all non-hidden people as fallback
  const people: string[] = [];
  if (!explicitlyHiddenPersonIds.has(marriageGroupData.spouse1.id)) {
    people.push(marriageGroupData.spouse1.id);
  }
  if (
    marriageGroupData.spouse2 &&
    !explicitlyHiddenPersonIds.has(marriageGroupData.spouse2.id)
  ) {
    people.push(marriageGroupData.spouse2.id);
  }
  return people;
}

/**
 * Computes which additional person IDs should be hidden when certain people are marked as hidden.
 * Uses person-level BFS to determine which people become truly orphaned (cannot reach any
 * non-hidden person who connects to a primary client) when hiding specific individuals.
 *
 * Key insight: Even if a marriage group node is excluded from rendering, the non-hidden
 * people in that group still "exist" and can serve as connection points for other nodes.
 *
 * @param nodes - All nodes in the family tree
 * @param edges - All edges connecting the nodes (from the visual diagram)
 * @param persons - Person data including primary and generation flags
 * @param nodeConfigs - Per-node configuration including showTile settings
 * @returns Set of person IDs that should be cascadingly hidden (NOT including explicitly hidden ones)
 */
export function computeCascadingHiddenNodes(
  nodes: FamilyTreeNode[],
  edges: Edge[],
  persons: Array<{ id: string; isPrimary: boolean; generation?: number }>,
  nodeConfigs: Record<string, FamilyTreeNodeConfig>
): Set<string> {
  // Find all person IDs that are explicitly hidden
  const explicitlyHiddenPersonIds = new Set<string>();
  Object.entries(nodeConfigs).forEach(([personId, config]) => {
    if (config.showTile === false) {
      explicitlyHiddenPersonIds.add(personId);
    }
  });

  // If no one is hidden, no cascading needed
  if (explicitlyHiddenPersonIds.size === 0) {
    return new Set<string>();
  }

  // Build person-to-person adjacency using the actual edges from the visual diagram
  // Key: person IDs can connect through marriage groups even if those groups can't render
  const personAdjacency = new Map<string, Set<string>>();

  // First, add implicit connections within marriage groups
  // Ex-spouses in a marriage group should be connected to their former spouse
  nodes.forEach((node) => {
    if (node.type === 'marriageGroup' && node.data) {
      const data = node.data as MarriageGroupData;

      // Helper to add bidirectional connection
      const addConnection = (personId1: string, personId2: string) => {
        // Skip if either person is hidden
        if (
          explicitlyHiddenPersonIds.has(personId1) ||
          explicitlyHiddenPersonIds.has(personId2)
        ) {
          return;
        }

        if (!personAdjacency.has(personId1)) {
          personAdjacency.set(personId1, new Set());
        }
        personAdjacency.get(personId1)!.add(personId2);

        if (!personAdjacency.has(personId2)) {
          personAdjacency.set(personId2, new Set());
        }
        personAdjacency.get(personId2)!.add(personId1);
      };

      // Connect spouse1 to spouse2 (if both exist)
      if (data.spouse2) {
        addConnection(data.spouse1.id, data.spouse2.id);
      }

      // Connect spouse1 to their ex-spouses
      data.spouse1ExSpouses.forEach((exSpouse) => {
        addConnection(data.spouse1.id, exSpouse.id);
      });

      // Connect spouse2 to their ex-spouses
      data.spouse2ExSpouses.forEach((exSpouse) => {
        if (data.spouse2) {
          addConnection(data.spouse2.id, exSpouse.id);
        }
      });
    }
  });

  edges.forEach((edge) => {
    // Skip hidden edges
    if (edge.style?.opacity === 0) return;

    // Find all non-hidden people in source and target nodes, respecting handles
    const sourceNode = nodes.find((n) => n.id === edge.source);
    const targetNode = nodes.find((n) => n.id === edge.target);

    if (!sourceNode || !targetNode) return;

    let sourcePeople: string[] = [];
    let targetPeople: string[] = [];

    // Extract people from source node (respecting sourceHandle)
    if (sourceNode.type === 'marriageGroup' && sourceNode.data) {
      const data = sourceNode.data as MarriageGroupData;
      sourcePeople = getPeopleForHandle(
        data,
        edge.sourceHandle ?? undefined,
        explicitlyHiddenPersonIds
      );
    } else if (sourceNode.type === 'person' && sourceNode.data?.person) {
      if (!explicitlyHiddenPersonIds.has(sourceNode.data.person.id)) {
        sourcePeople = [sourceNode.data.person.id];
      }
    }

    // Extract people from target node (respecting targetHandle)
    if (targetNode.type === 'marriageGroup' && targetNode.data) {
      const data = targetNode.data as MarriageGroupData;
      targetPeople = getPeopleForHandle(
        data,
        edge.targetHandle ?? undefined,
        explicitlyHiddenPersonIds
      );
    } else if (targetNode.type === 'person' && targetNode.data?.person) {
      if (!explicitlyHiddenPersonIds.has(targetNode.data.person.id)) {
        targetPeople = [targetNode.data.person.id];
      }
    }

    // If either side has no non-hidden people, skip this edge
    if (sourcePeople.length === 0 || targetPeople.length === 0) {
      return;
    }

    // Connect all source people to all target people (bidirectional)
    sourcePeople.forEach((sourcePerson) => {
      if (!personAdjacency.has(sourcePerson)) {
        personAdjacency.set(sourcePerson, new Set());
      }
      targetPeople.forEach((targetPerson) => {
        personAdjacency.get(sourcePerson)!.add(targetPerson);
      });
    });

    targetPeople.forEach((targetPerson) => {
      if (!personAdjacency.has(targetPerson)) {
        personAdjacency.set(targetPerson, new Set());
      }
      sourcePeople.forEach((sourcePerson) => {
        personAdjacency.get(targetPerson)!.add(sourcePerson);
      });
    });
  });

  // Find all primary client IDs (who are not hidden)
  const primaryClientIds = new Set<string>(
    persons
      .filter((p) => p.isPrimary && !explicitlyHiddenPersonIds.has(p.id))
      .map((p) => p.id)
  );

  // If no primary clients remain, don't cascade anything
  if (primaryClientIds.size === 0) {
    return new Set<string>();
  }

  // BFS from all non-hidden primary clients to find reachable people
  const reachablePeople = new Set<string>();
  const queue = Array.from(primaryClientIds);

  while (queue.length > 0) {
    const personId = queue.shift()!;

    if (reachablePeople.has(personId)) continue;

    reachablePeople.add(personId);

    const neighbors = personAdjacency.get(personId);
    if (neighbors) {
      neighbors.forEach((neighborId) => {
        if (
          !reachablePeople.has(neighborId) &&
          !explicitlyHiddenPersonIds.has(neighborId)
        ) {
          queue.push(neighborId);
        }
      });
    }
  }

  // Find all non-hidden people who are NOT reachable - these are orphaned
  const cascadingHiddenPersonIds = new Set<string>();

  persons.forEach((person) => {
    // Skip explicitly hidden people
    if (explicitlyHiddenPersonIds.has(person.id)) {
      return;
    }

    // Standalone non-family people do not participate in the family graph.
    if (person.generation === -99) {
      return;
    }

    // Skip primary clients (they're roots, can't be orphaned)
    if (person.isPrimary) {
      return;
    }

    // If person is not reachable from primary clients, they're orphaned
    if (!reachablePeople.has(person.id)) {
      cascadingHiddenPersonIds.add(person.id);
    }
  });

  return cascadingHiddenPersonIds;
}
